// ABOUTME: Header for the configuration page with title, timestamp, and save button.
// ABOUTME: Shows back navigation, last-saved timestamp, and pending changes count.

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function ConfigPageHeader({
  lastSaved,
  pendingChangesCount,
  onSave,
  onDiscard,
  onBack,
}) {
  const { t } = useTranslation();

  const formatTimestamp = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('hu-HU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const handleDiscardConfirm = () => {
    setDiscardDialogOpen(false);
    onDiscard();
  };

  return (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            onClick={onBack}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              color: colors.text.secondary,
              fontWeight: 500,
              fontSize: '0.85rem',
            }}
          >
            {t('config.backToDashboard')}
          </Button>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              color: colors.text.primary,
              fontSize: '1.4rem',
            }}>
              {t('config.title')}
            </Typography>
            {lastSaved && (
              <Typography sx={{
                fontSize: '0.75rem',
                color: colors.text.muted,
                mt: 0.25,
              }}>
                {t('config.lastSaved')}: {formatTimestamp(lastSaved)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {pendingChangesCount > 0 && (
            <Button
              onClick={() => setDiscardDialogOpen(true)}
              sx={{
                textTransform: 'none',
                color: colors.status.error,
                fontWeight: 500,
                fontSize: '0.85rem',
                borderRadius: '8px',
                border: `1px solid ${colors.status.error}`,
                px: 2,
                '&:hover': { backgroundColor: '#FEF2F2' },
              }}
            >
              {t('config.discard')}
            </Button>
          )}

          <Button
            variant="contained"
            onClick={onSave}
            disabled={pendingChangesCount === 0}
            sx={{
              textTransform: 'none',
              bgcolor: colors.ui.button,
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              '&:hover': { bgcolor: colors.ui.buttonHover },
              '&.Mui-disabled': { bgcolor: colors.border.light, color: colors.text.muted },
            }}
          >
            {t('config.save')}
            {pendingChangesCount > 0 && (
              <Badge
                badgeContent={pendingChangesCount}
                color="warning"
                sx={{ ml: 1.5 }}
              />
            )}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' }
        }}
      >
        <DialogTitle>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
            {t('config.discardConfirmTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem', color: colors.text.secondary }}>
            {t('config.discardConfirmDescription')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDiscardDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            {t('config.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDiscardConfirm}
            sx={{
              textTransform: 'none',
              bgcolor: colors.status.error,
              borderRadius: '8px',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            {t('config.discardConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
